/* eslint-disable @next/next/no-img-element */
import { Button, useMediaQuery } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { dataURLtoFile } from '../../common/cropImage';
import { imgAcceptTypeUpload } from '../../constant/fileType';
import { resizeImage } from '../../helpers/resizeImage';
import { FilledCameraIcon } from '../elements/svgIcon/filled';
import ChangeCoverImageModal from '../modal/training-module/ChangeCoverImageModal';

interface ICover {
  canEdit: boolean;
  value: string;
  onChange: (_: File) => Promise<unknown>;
  bottomClassname: string;
}

const Cover: React.FunctionComponent<ICover> = ({
  canEdit,
  value,
  onChange,
  bottomClassname,
}) => {
  const matches = useMediaQuery('(min-width:1024px)');
  const [changingCover, setChangingCover] = useState<boolean>(false);

  const uploadCoverRef = useRef<HTMLInputElement>(null);

  const [selectedCover, setSelectedCover] = useState<File | undefined>(
    undefined
  );
  const [previewCover, setPreviewCover] = useState<string | undefined>(
    undefined
  );
  const [openChangeCover, setOpenChangeCover] = useState<boolean>(false);

  const handleChangeCover = async () => {
    if (selectedCover) {
      try {
        await onChange(selectedCover);
        setChangingCover(false);
      } catch (error) {
        return;
      }
    }
  };

  const onSelectCover = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) {
      setSelectedCover(undefined);
      return;
    }
    const file = e.target.files?.item(0);
    if (!file) {
      return;
    }

    const minimizedImage: any = await resizeImage(file);
    const convertedToFile = dataURLtoFile(minimizedImage, 'user_cover.png');

    setSelectedCover(convertedToFile);
    setChangingCover(true);
  };

  useEffect(() => {
    if (!selectedCover) {
      setPreviewCover(undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(selectedCover);
    setPreviewCover(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedCover]);

  const coverOptions = !changingCover ? (
    <Button
      variant={matches ? 'medium_emphasis_v2' : 'rounded_medium_emphasis_v2'}
      className={`!absolute bottom-4 right-4 min-w-fit ${!matches && '!p-2'}`}
      onClick={() => {
        setOpenChangeCover(true);
      }}
    >
      <FilledCameraIcon />
      {matches && <p className="ml-1">Ảnh bìa</p>}
    </Button>
  ) : (
    <div className="absolute bottom-4 right-4 flex gap-x-1 text-xs">
      <div
        className="flex  h-fit w-fit cursor-pointer gap-x-1 rounded-md border-[1px] border-solid border-white bg-transparent px-[10px] py-1.5 text-white"
        onClick={() => {
          setChangingCover(false);
          setSelectedCover(undefined);
          setPreviewCover(undefined);
        }}
      >
        Hủy
      </div>
      <div
        className="flex  h-fit w-fit cursor-pointer gap-x-1 rounded-md border-[1px] border-solid border-[#242424] bg-[#242424] bg-origin-border px-[10px] py-1.5 text-white"
        onClick={handleChangeCover}
      >
        Lưu thay đổi
      </div>
    </div>
  );

  return (
    <>
      <input
        ref={uploadCoverRef}
        type="file"
        className="hidden"
        onChange={onSelectCover}
        accept={imgAcceptTypeUpload.join(', ')}
      />
      <div className={`relative ${bottomClassname}`}>
        <CoverWrapper src={previewCover || value}>
          {canEdit && coverOptions}
        </CoverWrapper>
      </div>
      <ChangeCoverImageModal
        open={openChangeCover}
        onClose={() => setOpenChangeCover(false)}
        confirmCallback={() => uploadCoverRef?.current?.click()}
      />
    </>
  );
};

export const CoverWrapper = ({
  children,
  src,
}: {
  children?: React.ReactNode;
  src: string;
}) => {
  return (
    <div className="relative flex aspect-[25/10] w-full items-center justify-center overflow-hidden md:rounded-[4px]">
      <img
        src={src}
        alt="cover"
        className="w-full object-fill"
        rel="preload"
        loading="lazy"
      />
      {children}
    </div>
  );
};

export default Cover;
